import { useEffect, useState } from 'react';
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RootState } from '../reducers';
import { useDispatch, useSelector } from 'react-redux';
import { setContext } from '@apollo/client/link/context';
import { Error } from '../../types/utils.types';
import { forceRefreshAccessToken } from '../actions/auth.actions';
import { triggerSessionExpiredHandler } from '../../utils/httpInterceptor';

interface GraphQLInterface {
  error: Error | undefined;
  loading: boolean;
  client?: ApolloClient<NormalizedCacheObject>;
}

const { REACT_APP_GRAPHQL_ENDPOINT } = process.env;

export const useGraphQL = (): GraphQLInterface => {
  const dispatch = useDispatch();
  const tokenUpdateCounter = useSelector((state: RootState) => state.app.tokenUpdateCounter);
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>();

  useEffect(() => {
    try {
      const idToken = sessionStorage.getItem('idToken');

      if (idToken) {
        const httpLink = new HttpLink({
          uri: REACT_APP_GRAPHQL_ENDPOINT,
        });

        const authLink = setContext((_, { headers }) => {
          const token = sessionStorage.getItem('idToken');
          return {
            headers: {
              ...headers,
              Authorization: `${token}`,
            },
          };
        });

        const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
          if (graphQLErrors) {
            for (const err of graphQLErrors) {
              if (
                err.extensions?.code === 'UNAUTHENTICATED' ||
                err.message.includes('Unauthorized') ||
                err.message.includes('Token expired')
              ) {
                const refreshToken = sessionStorage.getItem('refreshToken');
                if (refreshToken) {
                  dispatch(forceRefreshAccessToken(refreshToken));
                  return forward(operation);
                } else {
                  triggerSessionExpiredHandler();
                }
              }
            }
          }

          if (networkError && 'statusCode' in networkError) {
            if (networkError.statusCode === 401 || networkError.statusCode === 403) {
              const refreshToken = sessionStorage.getItem('refreshToken');
              if (refreshToken) {
                dispatch(forceRefreshAccessToken(refreshToken));
                return forward(operation);
              } else {
                triggerSessionExpiredHandler();
              }
            }
          }
        });

        const link = ApolloLink.from([errorLink, authLink, httpLink]);

        setClient(
          new ApolloClient({
            link,
            cache: new InMemoryCache({
              typePolicies: {
                BusinessMetadata: {
                  keyFields: ['asset_id'],
                },
                TechnicalMetadata: {
                  keyFields: false,
                },
                AssetMetadataResponse: {
                  keyFields: false,
                },
                AssetsResponse: {
                  keyFields: false,
                },
                AggregationBucket: {
                  keyFields: false,
                },
                LineageEdge: {
                  keyFields: ['edgeId'],
                },
                LineageNode: {
                  keyFields: ['_id'],
                },
              },
            }),
          })
        );
      }
    } catch (error) {
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  }, [tokenUpdateCounter]);

  return {
    error,
    loading,
    client,
  };
};
